import { EventType, FailureContext, generateEventTypeMapDefaults, WorkflowEventEventTypeMap, WorkflowEventMetaDataMapWithDefaults } from '@aiwyn/shared';

/**
 * Flow 8 — Payment Plans.
 * Covers create / update / deactivate for recurring payment plans.
 *
 * Each async op follows the SUBMITTED + SUCCEEDED + FAILED triad. Terminals carry
 * `operationDurationMs`; FAILED metadata extends `FailureContext`. Create and update share
 * `PAYMENT_PLAN_SUBMITTED` as the operation-start marker (the modal handles both modes);
 * deactivate's `PAYMENT_PLAN_DEACTIVATE_SUBMITTED` doubles as funnel-entry — the confirm-delete
 * modal fires the mutation as soon as the user confirms, so there's no earlier UI gesture
 * worth a separate marker.
 */
export enum PaymentPlanWorkflowEvent {
  PAYMENT_PLAN_CREATE_STARTED = 'PAYMENT_PLAN_CREATE_STARTED',
  PAYMENT_PLAN_PAYMENT_METHOD_SELECTED = 'PAYMENT_PLAN_PAYMENT_METHOD_SELECTED',
  PAYMENT_PLAN_SUBMITTED = 'PAYMENT_PLAN_SUBMITTED',
  PAYMENT_PLAN_CREATE_SUCCEEDED = 'PAYMENT_PLAN_CREATE_SUCCEEDED',
  PAYMENT_PLAN_CREATE_FAILED = 'PAYMENT_PLAN_CREATE_FAILED',
  PAYMENT_PLAN_UPDATE_STARTED = 'PAYMENT_PLAN_UPDATE_STARTED',
  PAYMENT_PLAN_UPDATE_SUCCEEDED = 'PAYMENT_PLAN_UPDATE_SUCCEEDED',
  PAYMENT_PLAN_UPDATE_FAILED = 'PAYMENT_PLAN_UPDATE_FAILED',
  PAYMENT_PLAN_DEACTIVATE_SUBMITTED = 'PAYMENT_PLAN_DEACTIVATE_SUBMITTED',
  PAYMENT_PLAN_DEACTIVATE_SUCCEEDED = 'PAYMENT_PLAN_DEACTIVATE_SUCCEEDED',
  PAYMENT_PLAN_DEACTIVATE_FAILED = 'PAYMENT_PLAN_DEACTIVATE_FAILED',
  PAYMENT_PLAN_CANCELLED = 'PAYMENT_PLAN_CANCELLED',
}

export type PaymentPlanWorkflowEventMetaDataMap = WorkflowEventMetaDataMapWithDefaults<
  PaymentPlanWorkflowEvent,
  {
    [PaymentPlanWorkflowEvent.PAYMENT_PLAN_PAYMENT_METHOD_SELECTED]: { paymentMethodType: string };
    [PaymentPlanWorkflowEvent.PAYMENT_PLAN_SUBMITTED]: { cadence: string; totalAmount: number };
    [PaymentPlanWorkflowEvent.PAYMENT_PLAN_CREATE_SUCCEEDED]: { planId: string; cadence: string; totalAmount: number; operationDurationMs: number };
    [PaymentPlanWorkflowEvent.PAYMENT_PLAN_CREATE_FAILED]: FailureContext & { operationDurationMs: number };
    [PaymentPlanWorkflowEvent.PAYMENT_PLAN_UPDATE_STARTED]: { planId: string };
    [PaymentPlanWorkflowEvent.PAYMENT_PLAN_UPDATE_SUCCEEDED]: { planId: string; operationDurationMs: number };
    [PaymentPlanWorkflowEvent.PAYMENT_PLAN_UPDATE_FAILED]: FailureContext & { planId: string; operationDurationMs: number };
    [PaymentPlanWorkflowEvent.PAYMENT_PLAN_DEACTIVATE_SUBMITTED]: { planId: string };
    [PaymentPlanWorkflowEvent.PAYMENT_PLAN_DEACTIVATE_SUCCEEDED]: { planId: string; operationDurationMs: number };
    [PaymentPlanWorkflowEvent.PAYMENT_PLAN_DEACTIVATE_FAILED]: FailureContext & { planId: string; operationDurationMs: number };
  }
>;

export const PaymentPlanEventEventTypeMap: WorkflowEventEventTypeMap<PaymentPlanWorkflowEvent> = {
  ...generateEventTypeMapDefaults(PaymentPlanWorkflowEvent, EventType.USER),
  [PaymentPlanWorkflowEvent.PAYMENT_PLAN_CREATE_SUCCEEDED]: EventType.SYSTEM,
  [PaymentPlanWorkflowEvent.PAYMENT_PLAN_UPDATE_SUCCEEDED]: EventType.SYSTEM,
  [PaymentPlanWorkflowEvent.PAYMENT_PLAN_DEACTIVATE_SUCCEEDED]: EventType.SYSTEM,
  [PaymentPlanWorkflowEvent.PAYMENT_PLAN_CREATE_FAILED]: EventType.ERROR,
  [PaymentPlanWorkflowEvent.PAYMENT_PLAN_UPDATE_FAILED]: EventType.ERROR,
  [PaymentPlanWorkflowEvent.PAYMENT_PLAN_DEACTIVATE_FAILED]: EventType.ERROR,
};
